<template>
  <COverflowWrapper :max-height="480">
    <NForm
      ref="formRef"
      class="theme-settings-step"
      :model="modelValue"
      :rules="formRules"
      :show-feedback="false"
      size="medium"
    >
      <NGrid :cols="2" :x-gap="12" :y-gap="24">
        <NFormItemGridItem :label="t('canvasUI.CThemeSettingsStep.name')" path="name" required>
          <NInput
            :placeholder="t('canvasUI.CThemeSettingsStep.namePlaceholder')"
            :value="modelValue.name"
            @update:value="(value: string) => emitUpdate({ name: value })"
          />
        </NFormItemGridItem>

        <NFormItemGridItem :label="t('canvasUI.CThemeSettingsStep.status')" path="status" required>
          <CSelect
            bordered
            :options="statusOptions"
            :value="modelValue.status"
            @update:value="(value: string) => emitUpdate({ status: value as CanvasThemeStatus })"
          />
        </NFormItemGridItem>

        <NFormItemGridItem
          :label="`${t('canvasUI.CThemeSettingsStep.componentSpacing')}: ${modelValue.componentSpacing}px`"
          path="componentSpacing"
          required
          :span="2"
        >
          <div class="flex items-center gap-2 w-full">
            <CSlider
              class="flex-1"
              :format-tooltip="(value: number) => `${value}px`"
              :step="DEFAULT_GLOBAL_COMPONENT_SPACING_INTERVAL"
              :value="modelValue.componentSpacing"
              @update:value="(value: number) => emitUpdate({ componentSpacing: value })"
            />
          </div>
        </NFormItemGridItem>

        <NFormItemGridItem :label="t('canvasUI.CThemeSettingsStep.availableFonts')" :span="2">
          <div class="flex flex-col items-center gap-2 w-full">
            <CSelect
              bordered
              :on-update:value="handleValueUpdate"
              :options="fontOptions"
              :placeholder="t('canvasUI.CTypographyConfiguratorItem.selectFont')"
              :render-label="renderFontLabel"
              :value="font"
            />

            <NUpload
              ref="uploadRef"
              :accept="SUPPORTED_FONT_EXTENSIONS.join(',')"
              :default-upload="false"
              :disabled="isSaving"
              :max="1"
              :multiple="false"
              :on-update:file-list="onFontSelectForUpload"
            >
              <NUploadDragger>
                <div>
                  <CIcon icon="upload" size="26px" />
                </div>
                <div class="mt-3">{{ t('canvasUI.CThemeSettingsStep.uploadHint') }}</div>
              </NUploadDragger>
            </NUpload>
            <CButton v-if="selectedFont" @click="uploadFont">
              <template #default>
                <NSpin v-if="isSaving" size="small" />
                <span v-else>{{ t('canvasUI.CThemeSettingsStep.uploadFont') }}</span>
              </template>
            </CButton>
            <div v-if="fileExists" class="text-sm color-error">
              {{ t('canvasUI.CThemeSettingsStep.fileExists', { font }) }}
            </div>
          </div>
        </NFormItemGridItem>
      </NGrid>
    </NForm>
  </COverflowWrapper>
</template>

<script setup lang="ts">
import type { AxiosInstance } from 'axios'
import { ref, computed, h, onMounted, nextTick, inject, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  NForm,
  NFormItemGridItem,
  NGrid,
  NInput,
  NUpload,
  NUploadDragger,
  type FormInst,
  type UploadFileInfo,
  NSpin,
  type UploadInst,
} from 'naive-ui'

import CButton from '@lib/components/CButton/CButton.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CSlider from '@lib/components/CSlider/CSlider.vue'
import COverflowWrapper from '@lib/components/COverflowWrapper/COverflowWrapper.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'

import { CanvasThemeStatus } from '@lib/types/canvasTheme'
import { DEFAULT_GLOBAL_COMPONENT_SPACING_INTERVAL } from '@canvas-builder/constants/theme.const'
import type { SelectOptionActionUrl } from '@lib/theme/theme.types'
import { SUPPORTED_FONT_EXTENSIONS } from '@lib/constants/uploads.const'
import { getFileNameWithoutExtension } from '@lib/apps/canvas-builder/util/file.util'
import { applyFont } from '@lib/util/font.util'
import { insertItemSorted } from '@lib/util/array'
import { type PitcherInfo } from '@lib/types/app'
import { type FileRetrieve, AccessTypeEnum } from '@openapi'

const props = defineProps<{
  modelValue: {
    name: string
    status: CanvasThemeStatus
    componentSpacing: number
  }
  fontOptions: SelectOptionActionUrl[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: typeof props.modelValue]
  'update:font': [font: SelectOptionActionUrl]
  validation: [isValid: boolean]
}>()

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)
const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios', ref())

const { t } = useI18n()
const { font, setFontAndFetchAsset } = useCanvasTheme()

const isSaving = ref(false)
const formRef = ref<FormInst | null>(null)
const uploadRef = ref<UploadInst | null>(null)
const fileExists = ref(false)
const selectedFont = ref<{ file: File; name: string }>()

const statusOptions = [
  {
    label: t('canvasUI.CThemeSettingsStep.statusOptions.draft'),
    value: CanvasThemeStatus.DRAFT,
  },
  {
    label: t('canvasUI.CThemeSettingsStep.statusOptions.ready'),
    value: CanvasThemeStatus.READY,
  },
]

const formRules = computed(() => ({
  name: [{ required: true }],
  status: [{ required: true }],
  componentSpacing: [{ required: true }, { type: 'number' as const, min: 0 }, { type: 'number' as const, max: 100 }],
}))

async function emitUpdate(updates: Partial<typeof props.modelValue>) {
  const updatedValue = {
    ...props.modelValue,
    ...updates,
  }

  emit('update:modelValue', updatedValue)

  nextTick(() => {
    validate(updatedValue)
  })
}

async function handleValueUpdate(value: string, option: SelectOptionActionUrl) {
  await setFontAndFetchAsset(value, option.url as string)
  font.value = value
}

const renderFontLabel = (option: SelectOptionActionUrl) => {
  return h('div', { class: 'select-option' }, [
    h(
      'span',
      {
        style: `font-family: ${option.value};`,
      },
      option.label,
    ),
  ])
}

function checkIfFileExists(fileName: string | undefined): boolean {
  if (!fileName) return false

  const name = getFileNameWithoutExtension(fileName)
  return props.fontOptions.some((item) => item.label === name)
}

function onFontSelectForUpload(fileList: UploadFileInfo[]) {
  const file = fileList?.[0]?.file

  fileExists.value = checkIfFileExists(file?.name)
  if (fileExists.value) {
    font.value = file?.name ?? ''
    return false
  }

  if (!file) {
    selectedFont.value = undefined
    return
  }

  const fontFamilyName = file.name
    .replace(/\.[^.]*$/, '')
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')

  selectedFont.value = { file, name: fontFamilyName }
  const reader = new FileReader()
  reader.onload = (e: ProgressEvent<FileReader>) => {
    const fontDataUrl = e.target ? e.target.result : ''

    applyFont(fontDataUrl, fontFamilyName)

    !props.fontOptions.find((f) => f.value === fontFamilyName) &&
      insertItemSorted(props.fontOptions, { label: fontFamilyName, value: fontFamilyName }, (a: any, b: any) =>
        a.value.localeCompare(b.value),
      )

    font.value = fontFamilyName
  }
  reader.readAsDataURL(file)
}

function validate(value = props.modelValue) {
  const { name, status, componentSpacing } = value
  const isValid = !!(name && status && componentSpacing >= 0)
  emit('validation', isValid)
}

async function uploadFont() {
  if (!pitcherInfo.value) throw new Error('Pitcher info is not provided - CanvasDrawerTheme.vue')
  if (!selectedFont.value) throw new Error('Font is not selected')
  if (!restApiAxios.value) throw new Error('Rest API Axios is not provided')

  const formData = new FormData()
  formData.append('content', selectedFont.value.file)
  formData.append('name', selectedFont.value.name)
  formData.append('instance_id', pitcherInfo.value.instanceId)
  formData.append('access_type', AccessTypeEnum.PUBLIC)
  formData.append('is_asset', 'true')
  isSaving.value = true
  const response = await restApiAxios.value.post<unknown, FileRetrieve>('files/', formData)
  isSaving.value = false

  // Clear the file selection from the upload component
  if (uploadRef.value) {
    uploadRef.value.clear()
  }

  selectedFont.value = undefined
  fileExists.value = false

  const fontUpdate: SelectOptionActionUrl = {
    label: response.name ?? '',
    value: response.name ?? '',
    url: response.content_url,
  }

  emit('update:font', fontUpdate)

  return response
}

onMounted(() => {
  validate()
})
</script>
